/**
 * Profile Completion Page
 * Shown after Canva OAuth for new users — collects full name and company email
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Button, Typography, Alert,
  TextField, CircularProgress,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export default function ProfileCompletionPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Store token from URL so API calls can use it
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      localStorage.setItem('access_token', token);
    } else if (!localStorage.getItem('access_token')) {
      navigate('/login');
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim()) {
      setError('Both fields are required');
      return;
    }
    if (!email.toLowerCase().endsWith('@masholdings.com')) {
      setError('Email must be from @masholdings.com domain');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await api.put('/auth/profile', { full_name: fullName.trim(), email: email.trim().toLowerCase() });
      // Refresh user state so profile_completed=true before navigating
      await refreshUser();
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #424242 0%, #757575 50%, #D32F2F 100%)',
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={24} sx={{ p: 5, borderRadius: 2 }}>
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Box sx={{ display: 'inline-flex', p: 2, bgcolor: 'primary.50', borderRadius: '50%', mb: 2 }}>
              <PersonIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Complete Your Profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please enter your details to start using the application.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              required
              label="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              sx={{ mb: 3 }}
              autoFocus
            />
            <TextField
              fullWidth
              required
              label="Company Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              helperText="Must be your @masholdings.com email"
              sx={{ mb: 4 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ py: 1.5, fontWeight: 600 }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Continue'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
